import { useState, type ReactNode } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type components } from "@njbsoft/types";
import { useAppStore } from "@/store";
import { usePermissions } from "@/hooks/use-permissions";
import { useDeviceInventoryDetail, useInstallationChain } from "@/hooks/use-device-inventory";
import { useDeviceInstallationDetail } from "@/hooks/use-device-installations";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { useNeedsAttentionRollup, rollupToMap } from "@/hooks/use-needs-attention-rollup";
import { useCatalogAssemblyListing } from "@/hooks/use-catalog-assemblies";
import { InstallationChainTable } from "@/components/device-inventory/installation-chain-table";
import { ReplaceInstallationDrawer } from "@/components/device-installations/replace-installation-drawer";
import { type InstallationDrawerSubject } from "@/components/device-installations/installation-prior-summary-card";
import {
  PageHeader,
  Card,
  CardContent,
  Skeleton,
  EmptyValue,
  Badge,
  Button,
  NeedsAttentionFlag,
  VerdictBadge,
} from "@/components/ui";
import { AccountCard } from "@/components/compliance/account-summary";
import { EntityDocuments } from "@/components/documents/entity-documents";

type DeviceInventory = components["schemas"]["DeviceInventory"];
type InstallationChainRow = components["schemas"]["InstallationChainRow"];
type DeviceInstallation = components["schemas"]["DeviceInstallation"];

/**
 * The tracked asset + its in-service chain row as a lifecycle-drawer subject.
 * The register carries no `device_category`, so the prior's category is
 * derived from its own identity: a populated `non_testable_subtype` marks a
 * non-testable device, every other registered asset is a testable assembly
 * (air gaps carry no serial identity and never enter the serial-keyed
 * register). The derivation feeds the prior-summary card's category badge —
 * the one-form drawer's wire category comes from the chosen arm (finder
 * pick, escape adapter, or the untouched-form inherit), never from this
 * display value. Neither row carries a test of
 * record, so the last-test members are null (the summary card renders
 * `EmptyValue` for them); the chain row is the in-service row by the
 * find predicate, hence the literal status. `verdict` is the register's
 * active-installation verdict; null falls to `unverified`, the union's own
 * no-test arm. The chain row carries no schedule, so the due dates — the live
 * one, else the one saved while testing is paused, which a successor carries —
 * come off the in-service installation's own read.
 */
function subjectFromInventoryRow(
  row: DeviceInventory,
  currentInstallation: InstallationChainRow,
  dues: Pick<DeviceInstallation, "next_test_due_date" | "paused_next_test_due_date">,
): InstallationDrawerSubject {
  return {
    installationId: currentInstallation.id,
    priorInventoryId: row.id,
    serialNumber: row.serial_number,
    manufacturerLabel: row.manufacturer_label ?? null,
    modelLabel: row.model_label ?? null,
    sizeInches: row.size_inches,
    // Truthiness is the null+undefined test: the subtype is a closed enum, so
    // "" is never a member.
    deviceCategory: row.non_testable_subtype ? "non_testable_device" : "testable_assembly",
    deviceStatus: "in_service",
    verdict: row.verdict ?? "unverified",
    lastTestCompletedAt: null,
    lastTestPassed: null,
    facility: {
      id: currentInstallation.facility_id,
      name: currentInstallation.facility_name ?? null,
      address: null,
    },
    listingSource: row.listing_source,
    grandfatherBasis: row.grandfather_basis ?? null,
    assemblyTypeSelfDeclared: row.assembly_type_self_declared ?? null,
    nonTestableSubtype: row.non_testable_subtype ?? null,
    nextTestDueDate: dues.next_test_due_date ?? null,
    pausedNextTestDueDate: dues.paused_next_test_due_date ?? null,
  };
}

export const Route = createFileRoute("/_authenticated/temporary-devices/$id/")({
  component: DeviceInventoryDetailPage,
});

function DeviceInventoryDetailPage() {
  const { id } = Route.useParams();
  const { t } = useTranslation(["device-inventory", "org"]);
  const orgId = useAppStore((s) => s.activeOrgId);
  const { can } = usePermissions();
  const navigate = useNavigate();
  // One drawer session: subject and open set together (the grid mounts'
  // contract) — the drawer's success panel owns navigation to the successor's
  // installation detail; nothing here routes on completion.
  const [replaceSubject, setReplaceSubject] = useState<InstallationDrawerSubject | null>(null);

  const detail = useDeviceInventoryDetail(id);
  useRecentPageTitle(detail.data?.serial_number ?? undefined);
  const chain = useInstallationChain(id);
  // The DeviceInventory row does not expose `current_installation_id`
  // directly. Resolve it from the chain: the in_service row is the
  // active installation (at most one per AWWA M14 §6.5 attach-vs-
  // transfer invariant + DB partial UNIQUE
  // `idx_device_installations_one_in_service_per_inventory`).
  const currentInstallation =
    chain.data?.items.find((r) => r.device_status === "in_service") ?? null;
  // The replace subject's next test due date; disabled with no in-service row.
  const currentDetail = useDeviceInstallationDetail(currentInstallation?.id ?? "");
  // Informational only — the operator is already on the detail page, so the
  // flag is a non-clickable indicator (the installation chain Card below
  // surfaces the affected installation).
  const { data: naRollup } = useNeedsAttentionRollup("inventory", [id]);
  const naItem = rollupToMap(naRollup).get(id);
  // Resolve the catalog assembly listing to navigate the Model link to the
  // PARENT assembly route — the assemblies route is keyed by assembly id, not
  // listing id, so linking the listing id directly would 404. The hook no-ops
  // (disabled) when the device is freetext/unlisted (null listing id).
  const listing = useCatalogAssemblyListing(detail.data?.catalog_assembly_listing_id ?? null);

  if (!orgId) {
    return (
      <div className="p-8">
        <p className="text-muted">{t("noOrgContext", { ns: "org" })}</p>
      </div>
    );
  }

  if (detail.isLoading) {
    return (
      <div className="p-8">
        <Skeleton className="h-10 w-80" />
        <div className="mt-6 space-y-3">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (detail.isError || !detail.data) {
    return (
      <div className="p-8">
        <p className="text-danger">{t("device-inventory:errors.loadDetailFailed")}</p>
      </div>
    );
  }

  const row = detail.data;
  const isRetired = !!row.retired_at;

  // Manufacturer links to the catalog manufacturer detail when the device is
  // catalog-anchored (manufacturer_id present); freetext/unlisted devices render
  // the resolved label as plain text.
  const manufacturerValue: ReactNode = row.manufacturer_id ? (
    <Link
      to="/catalog/manufacturers/$id"
      params={{ id: row.manufacturer_id }}
      className="text-primary hover:underline"
    >
      {row.manufacturer_label}
    </Link>
  ) : (
    row.manufacturer_label
  );

  // Model links to the PARENT assembly detail (resolved from the listing). Plain
  // text while the listing resolves, when it errors, and when the device has no
  // catalog listing id (freetext/unlisted).
  const modelValue: ReactNode =
    row.catalog_assembly_listing_id && listing.data ? (
      <Link
        to="/catalog/assemblies/$id"
        params={{ id: listing.data.parent_assembly_id }}
        className="text-primary hover:underline"
      >
        {row.model_label}
      </Link>
    ) : (
      row.model_label
    );

  // Under the installation-primary model the device-owner axis lives on the
  // installation row, NOT the inventory row — the inventory detail surfaces no
  // ownership card.

  // Edit mutates the inventory register row; Replace is a cross-entity op that
  // supersedes the in-service installation, so it rides the installation grant.
  const canEdit = can("device_inventory", "update") && !isRetired;
  const currentRow = currentDetail.data;
  const canReplace =
    can("device_installations", "replace") &&
    !isRetired &&
    currentInstallation !== null &&
    currentRow !== undefined;

  return (
    <div className="p-8">
      <PageHeader
        title={t("device-inventory:detail.title")}
        subtitle={row.serial_number}
        meta={
          <div className="flex items-center gap-2">
            {row.verdict ? (
              <VerdictBadge verdict={row.verdict} />
            ) : (
              <Badge variant="default">{t("device-inventory:currentInstallation.none")}</Badge>
            )}
            <NeedsAttentionFlag count={naItem?.total} pendingCount={naItem?.pending_review} />
          </div>
        }
        actions={
          canEdit || canReplace ? (
            <div className="flex gap-2">
              {canEdit && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    void navigate({
                      to: "/temporary-devices/$id/edit",
                      params: { id },
                    })
                  }
                >
                  {t("device-inventory:detail.editCta")}
                </Button>
              )}
              {canReplace && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    currentInstallation &&
                    currentRow &&
                    setReplaceSubject(subjectFromInventoryRow(row, currentInstallation, currentRow))
                  }
                >
                  {t("device-inventory:detail.replaceCta")}
                </Button>
              )}
            </div>
          ) : undefined
        }
      />

      {isRetired && (
        <div className="mt-4">
          <Badge variant="default">{t("device-inventory:detail.retiredBadge")}</Badge>
        </div>
      )}

      <div className="mt-6 space-y-6">
        <Card title={t("device-inventory:detail.sectionIdentity")}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <DetailRow
              label={t("device-inventory:fields.serialNumber")}
              value={row.serial_number}
            />
            <DetailRow
              label={t("device-inventory:fields.manufacturer")}
              value={manufacturerValue}
            />
            <DetailRow label={t("device-inventory:fields.model")} value={modelValue} />
            <DetailRow
              label={t("device-inventory:fields.sizeInches")}
              value={String(row.size_inches)}
            />
          </div>
        </Card>

        <Card title={t("device-inventory:detail.sectionListing")}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <DetailRow
              label={t("device-inventory:fields.listingSource")}
              value={t(`device-inventory:listingSource.${row.listing_source}`)}
            />
            <DetailRow
              label={t("device-inventory:fields.grandfatherBasis")}
              value={row.grandfather_basis}
            />
            <DetailRow
              label={t("device-inventory:fields.assemblyTypeSelfDeclared")}
              value={row.assembly_type_self_declared}
            />
            <DetailRow
              label={t("device-inventory:fields.nonTestableSubtype")}
              value={row.non_testable_subtype}
            />
            <DetailRow
              label={t("device-inventory:fields.alternateListingBody")}
              value={row.alternate_listing_body}
            />
            <DetailRow
              label={t("device-inventory:fields.alternateListingStandard")}
              value={row.alternate_listing_standard}
            />
            <DetailRow
              label={t("device-inventory:fields.alternateListingIdOrFileNumber")}
              value={row.alternate_listing_id_or_file_number}
            />
            <DetailRow
              label={t("device-inventory:fields.stateApprovalJurisdiction")}
              value={row.state_approval_jurisdiction}
            />
          </div>
        </Card>

        <AccountCard
          accountId={row.resolved_account_id ?? null}
          accountName={row.resolved_account_name ?? null}
        />

        <Card title={t("device-inventory:installationChain.title")}>
          <CardContent>
            {chain.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : (
              <InstallationChainTable
                rows={chain.data?.items ?? []}
                onRowNavigate={(installationId) =>
                  void navigate({
                    to: "/device-installations/$id",
                    params: { id: installationId },
                  })
                }
              />
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 space-y-6">
        {orgId && <EntityDocuments entityType="device_inventory" entityId={id} orgId={orgId} />}
      </div>

      {replaceSubject !== null && (
        <ReplaceInstallationDrawer
          open
          onClose={() => setReplaceSubject(null)}
          subject={replaceSubject}
        />
      )}
    </div>
  );
}

interface DetailRowProps {
  label: string;
  value: ReactNode;
}

function DetailRow({ label, value }: DetailRowProps) {
  // String/nullish values fall back to EmptyValue when blank; non-string nodes
  // (e.g. catalog Links) render verbatim. A link whose own children are null is
  // the caller's responsibility to avoid.
  const isEmptyString =
    value === null ||
    value === undefined ||
    (typeof value === "string" && value.trim().length === 0);
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-muted">{label}</p>
      <p className="mt-1 text-sm text-on-surface">{isEmptyString ? <EmptyValue /> : value}</p>
    </div>
  );
}
